import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { SessionGuard } from '../components/settings/SessionGuard';
import { DspCompliance } from '../components/dsp/DspCompliance';
import { GatesCard, ProviderCard, SubscriptionsCard } from '../components/dsp/PlatformDspAdmin';
import { platformAPI } from '../lib/dspAPI';
import { PlatformMfaCard } from '../components/dsp/PlatformMfaCard';
import { PracticesCard } from '../components/dsp/PracticesCard';
import { NotPlatformPanel, leavePlatform } from '../components/dsp/PlatformSessionSwitch';

const Grants = ({ rows, start }) => (
  <Card><CardHeader><CardTitle>Support access granted to you</CardTitle>
    <CardDescription>You can only open an organisation when its owner has granted time-limited access. Every request is audited.</CardDescription></CardHeader>
    <CardContent>{rows.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="platform-no-grants">No active grants.</p> : rows.map((g) => (
      <div key={g.id} className="flex items-center gap-2 border-b py-2 text-sm" data-testid={`platform-grant-${g.id}`}>
        <span className="font-medium flex-1">{g.organization_name}</span><Badge variant="outline">{g.scope}</Badge>
        <span className="text-xs text-muted-foreground">until {new Date(g.expires_at).toLocaleString('en-AU')}</span>
        <Button size="sm" onClick={() => start(g)} data-testid={`platform-grant-start-${g.id}`}>Open</Button>
      </div>))}</CardContent></Card>
);

// Platform (DSP) admin console: support grants, ATO subscriptions, production gates, DSP compliance registers.
export default function PlatformAdmin() {
  const [rows, setRows] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [org, setOrg] = useState(null);
  const [tab, setTab] = useState('practices');
  const [pending, setPending] = useState(null);
  const [mfaOn, setMfaOn] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [who, setWho] = useState(null);
  const [params] = useSearchParams();
  const focus = params.get('practice');
  const navigate = useNavigate();
  const loadMfa = () => api.get('/api/mfa/status').then((r) => setMfaOn(!!r.data.enabled)).catch(() => {});
  const isPlatform = who?.session_role === 'platform_admin';
  useEffect(() => {
    if (!sessionStorage.getItem('access_token')) {  // e.g. email link opened in a new tab (sessions are per tab)
      sessionStorage.setItem('kv_return_to', window.location.pathname + window.location.search);
      navigate('/login', { replace: true });
      return;
    }
    api.get('/api/auth/platform-session').then((r) => setWho(r.data)).catch(() => setWho({ session_role: 'platform_admin' }));
  }, [navigate, reloadKey]);
  useEffect(() => {
    if (!isPlatform) return;
    loadMfa();
    api.get('/api/platform/support-grants').then((r) => setRows(r.data.grants)).catch((e) => { if (e.response?.data?.detail !== 'MFA_SETUP_REQUIRED') toast.error(e.response?.data?.detail || 'Failed to load'); });
    platformAPI.subs().then((d) => setOrgs(d.organizations)).catch(() => {});
  }, [isPlatform, reloadKey]);
  useEffect(() => { if (focus) setTab('practices'); }, [focus]);
  const onMfaEnabled = () => { setReloadKey((k) => k + 1); setTab('practices'); };
  const start = (g) => api.post(`/api/platform/support-grants/${g.id}/start`).then((r) => {
    sessionStorage.setItem('platform_token', sessionStorage.getItem('access_token'));
    sessionStorage.setItem('access_token', r.data.access_token);
    toast.success(`Support session started for ${g.organization_name}`);
    navigate('/dashboard');
  }).catch((e) => toast.error(e.response?.data?.detail || 'Could not start session'));
  const logout = () => { api.post('/api/auth/logout').finally(() => { sessionStorage.clear(); navigate('/login'); }); };
  const pickOrg = (o) => { setOrg(o); setTab('gates'); };
  return (
    <div className="p-8 max-w-6xl mx-auto space-y-6" data-testid="platform-admin-page">
      <SessionGuard onLogout={logout} />
      <div className="flex items-center gap-2"><h1 className="text-3xl font-bold flex-1">Platform administration</h1>
        {isPlatform && who.has_membership && <Button variant="outline" onClick={() => leavePlatform(navigate)} data-testid="platform-leave-console">Back to my practice</Button>}
        <Button variant="outline" onClick={logout} data-testid="platform-logout">Sign out</Button></div>
      {who && !isPlatform && <NotPlatformPanel who={who} navigate={navigate} onEntered={() => setReloadKey((k) => k + 1)} />}
      {isPlatform && <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="practices" data-testid="platform-tab-practices">Practices
            {pending > 0 && <Badge className="ml-2 h-5 px-1.5" data-testid="platform-tab-practices-count">{pending}</Badge>}</TabsTrigger>
          <TabsTrigger value="dsp" data-testid="platform-tab-dsp">DSP Compliance</TabsTrigger>
          <TabsTrigger value="subs" data-testid="platform-tab-subs">ATO subscriptions</TabsTrigger>
          <TabsTrigger value="gates" data-testid="platform-tab-gates">Production gate</TabsTrigger>
          <TabsTrigger value="grants" data-testid="platform-tab-grants">Support access</TabsTrigger>
          <TabsTrigger value="security" data-testid="platform-tab-security">My security</TabsTrigger>
        </TabsList>
        <TabsContent value="practices" className="mt-4"><PracticesCard highlightId={focus} onCount={setPending} reloadKey={reloadKey} mfaMissing={mfaOn === false} onOpenSecurity={() => setTab('security')} /></TabsContent>
        <TabsContent value="dsp" className="mt-4"><DspCompliance platform org={org} /></TabsContent>
        <TabsContent value="subs" className="mt-4 space-y-4"><ProviderCard /><SubscriptionsCard onPickOrg={pickOrg} /></TabsContent>
        <TabsContent value="gates" className="mt-4"><GatesCard org={org} orgs={orgs} onOrg={setOrg} /></TabsContent>
        <TabsContent value="grants" className="mt-4"><Grants rows={rows} start={start} /></TabsContent>
        <TabsContent value="security" className="mt-4"><PlatformMfaCard onEnabled={onMfaEnabled} /></TabsContent>
      </Tabs>}
    </div>
  );
}
